// libraries
import React, { ReactNode, useState } from 'react';

import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import styled from 'styled-components';

// styles and components
import { expandCard } from './helpers';

import { COLORS } from '../../../utils/theme/colors';
import { HeaderIcon2 } from '../../molecules/HeaderIcon2/HeaderIcon2';
import { Pagination } from '../../molecules/Pagination/Pagination';

// functions

interface ICardListConstructor {
  childRef: any;
  paginationCallback?: (newList: any) => void;
  pureData?: any[];
  InnerComponent: React.FC<any>;
  data: {
    header: {
      accessor: any;
      text: string;
      size: string | '1%';
      dropdown: ReactNode;
    }[];
    cardsList: {
      value: string;
      subValue: string;
      icon: any;
      textColor: string;
      size: string | '1%';
    }[][];
    innerData: any[];
  };
  riskText?: any;
  noOrderHeader?: boolean;
  checkboxAvailable?: any;
  selectMode?: any;
  checkedList?: any;
  setCheckedList?: any;
  count?: any;
  currentPage?: any;
  setCurrentPage?: any;
  pageSize?: any;
  headerFilter?: any;
  setHeaderFilter?: any;
}

const CardListConstructor: React.FC<ICardListConstructor> = ({
  data,
  InnerComponent,
  riskText = false,
  checkboxAvailable = false,
  selectMode = false,
  checkedList = [],
  setCheckedList = [],
  count,
  currentPage,
  setCurrentPage,
  pageSize,
  noOrderHeader,
  headerFilter,
  setHeaderFilter,
}) => {
  const { header, cardsList, innerData } = data;
  const [selected, setselected] = useState(0);

  const headerOrder = ['desc', 'asc', 'null'];
  const headerOrderLimit = 2;

  const cardsListLimited = cardsList?.slice(0, 10);

  const checkingItem = (card, e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      setCheckedList((checkedList) => [...checkedList, card[0]?.value]);
    } else {
      setCheckedList(checkedList.filter((item) => item !== card[0]?.value));
    }
  };

  const handleOrderHeader = (header) => {
    if (
      headerFilter.length > 0 &&
      headerFilter.filter((i) => i.key == header.accessor).length > 0
    ) {
      const newHeaderFilter = headerFilter.map((h) => {
        if (h.key === header.accessor) {
          return {
            ...h,
            value:
              headerOrder[
                headerOrder.indexOf(h.value) + 1 > headerOrderLimit
                  ? 0
                  : headerOrder.indexOf(h.value) + 1
              ],
          };
        }
        return h;
      });
      setHeaderFilter(newHeaderFilter);
    } else {
      setHeaderFilter([
        ...headerFilter,
        { key: header.accessor, value: headerOrder[0] },
      ]);
    }
  };

  return (
    <Div>
      <div className='header_list_constructor'>
        {header?.map((item) => {
          return (
            <div
              key={item.text}
              className='header_item cursor'
              style={{ flexBasis: item.size }}
              onClick={() => {
                if (!noOrderHeader) {
                  handleOrderHeader(item);
                }
              }}
            >
              {item.text}
              <div className='header-icon-container'>
                <HeaderIcon2
                  type={
                    headerFilter.find((obj) => {
                      return obj.key == item.accessor;
                    })?.value
                  }
                />
              </div>
              {item.dropdown}
            </div>
          );
        })}
      </div>
      <div className='cards_list'>
        {cardsListLimited?.map((card, index) => {
          const [cardWithTextColor]: any = card.filter(
            (card: any) => card?.textColor,
          );

          return (
            <div
              style={{ display: 'flex', position: 'relative' }}
              key={'card' + index}
            >
              {checkboxAvailable && selectMode && (
                <Checkbox
                  className='checkbox'
                  checked={
                    checkedList.indexOf(card[0].value) > -1 ? true : false
                  }
                  disabled={cardWithTextColor.textColor !== '#fd5d5d'}
                  onChange={(e) => {
                    checkingItem(card, e);
                  }}
                />
              )}
              <div
                className='card_in_list cursor'
                onClick={(e) => {
                  setselected(index);
                  expandCard(e);
                }}
              >
                <div className='card_header'>
                  {card.map(
                    ({ icon, subValue, value, size, textColor }, index) => {
                      return (
                        <div
                          key={'value-' + index}
                          className='card_values'
                          style={{ flexBasis: size }}
                        >
                          {icon}
                          <div className='card_value_label'>
                            <p
                              className='main_card_text'
                              style={{
                                color: textColor,
                                textDecoration: riskText
                                  ? 'line-through'
                                  : undefined,
                              }}
                            >
                              {value}
                            </p>
                            <p className='sub_card_text'>{subValue}</p>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
                {selected === index && (
                  <InnerComponent
                    {...{
                      className: 'card_inner_content',
                      index,
                      data: innerData,
                    }}
                  />
                )}
                {/* The component above is receiving the index of the list. In the MOCK data, there is a (cardsList) and (innerData) property, those two must be synchronized with their index.*/}
              </div>
            </div>
          );
        })}
        {
          <Pagination
            className='pagination-bar'
            currentPage={currentPage}
            totalCount={count}
            pageSize={pageSize}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
          />
        }
      </div>
    </Div>
  );
};

export default CardListConstructor;

const Div = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  color: #444;

  .header_list_constructor {
    position: sticky;
    top: 64px;
    left: 0;
    height: 50px;
    width: 97%;
    margin: 25px auto -2px;
    display: flex;
    flex-direction: row;
    font-weight: 400;
    font-size: 14px;
    color: #444;
    z-index: 4;
    pointer-events: none;
    background-color: #fff;
  }

  .header_item {
    display: flex;
    align-items: center;
    padding-right: 5px;
    padding-left: 20px;
    border-radius: 8px;
    height: 50px;
    user-select: none;
    pointer-events: all;
    transition: background-color 200ms ease-in-out;
    position: relative;
    font-weight: 700;

    :hover {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }

    :hover .polygon path {
      fill: ${COLORS.primary};
    }

    .connector_header_item_clicked {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }
  }

  .card_in_list {
    width: 97%;
    height: 55px;
    margin: 5px auto;
    box-sizing: border-box;
    box-shadow: 0 0 4px 0px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    background-color: #fff;
    overflow: hidden;

    :hover {
      box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.1);
      transform: scale(1.007);
    }

    /* O card aberto cresce com o conteúdo: uma transação com ociosidade ganha o
       bloco de ociosidade e o botão de cancelamento, e com altura fixa os botões
       do fim (Download Meter Values, Parar carregamento) caíam fora do overflow.
       O min-height preserva o tamanho anterior nos cards sem ociosidade. */
    &.gabrr_expanded {
      position: sticky;
      top: 130px;
      left: 0;
      height: auto;
      min-height: 350px;
      padding-bottom: 16px;
      z-index: 3;
    }

    &.gabrr_expanded .card_inner_content {
      height: auto;
      width: 100%;
      z-index: 3;
    }
  }

  .card_header {
    display: flex;
    flex-direction: row;
    height: 55px;
    user-select: none;

    .card_values {
      padding-left: 20px;
      display: flex;
      align-items: center;
    }

    .card_value_label {
      .main_card_text {
        margin: 0;
        font-size: 14px;
        font-weight: 400;
      }
      .sub_card_text {
        margin: 0;
        font-size: 10px;
        font-weight: 400;
      }
    }
  }

  .checkbox {
    display: flex;
    position: absolute;
    top: 40%;
  }

  .pagination-bar {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    padding-right: 20px;
  }
`;
